import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { getWritingStyles, deleteWritingStyle } from '@/api/writingStyles';
import { getBrandProfiles, deleteBrandProfile } from '@/api/brands';
import { getBrandProfileBranches } from '@/api/brandBranches';
import type { PersonalWritingStyle, BrandProfile, BrandProfileBranch } from '@/types';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

export type WritingStyleItem =
  | (PersonalWritingStyle & { profileType: 'personal' })
  | (BrandProfile & { profileType: 'brand' });

interface WritingStylesContextType {
  // Data
  personalStyles: PersonalWritingStyle[];
  brandProfiles: BrandProfile[];
  brandProfileBranches: BrandProfileBranch[];
  allItems: WritingStyleItem[];
  totalCount: number;

  // Loading state
  loading: boolean;

  // Actions
  loadStyles: () => Promise<void>;
  deleteStyle: (id: string, type: 'personal' | 'brand') => Promise<void>;
  refreshStyles: () => Promise<void>;

  // Filtering
  getFilteredItems: (filter: 'all' | 'personal' | 'brands') => WritingStyleItem[];

  // Helper functions
  getStyleById: (id: string, type: 'personal' | 'brand') => PersonalWritingStyle | BrandProfile | undefined;
  getBranchById: (id: string) => BrandProfileBranch | undefined;
}

const WritingStylesContext = createContext<WritingStylesContextType | undefined>(undefined);

interface WritingStylesProviderProps {
  children: ReactNode;
}

export const WritingStylesProvider: React.FC<WritingStylesProviderProps> = ({ children }) => {
  const [personalStyles, setPersonalStyles] = useState<PersonalWritingStyle[]>([]);
  const [brandProfiles, setBrandProfiles] = useState<BrandProfile[]>([]);
  const [brandProfileBranches, setBrandProfileBranches] = useState<BrandProfileBranch[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();

  // Load all writing styles, brand profiles, and branches
  const loadStyles = useCallback(async () => {
    if (!user) {
      setPersonalStyles([]);
      setBrandProfiles([]);
      setBrandProfileBranches([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [personalData, brandData, branchData] = await Promise.all([
        getWritingStyles(),
        getBrandProfiles(),
        getBrandProfileBranches(),
      ]);
      setPersonalStyles(personalData);
      setBrandProfiles(brandData);
      setBrandProfileBranches(branchData);
    } catch (error) {
      console.error('Error loading writing styles:', error);
      toast.error('Failed to load writing styles');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Delete a writing style or brand profile
  const deleteStyle = useCallback(async (id: string, type: 'personal' | 'brand') => {
    try {
      if (type === 'personal') {
        await deleteWritingStyle(id);
        toast.success('Personal writing style deleted');
      } else {
        await deleteBrandProfile(id);
        toast.success('Brand profile deleted');
      }
      // Reload styles after deletion
      await loadStyles();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete writing style');
      throw error;
    }
  }, [loadStyles]);

  // Refresh styles (alias for loadStyles for clarity)
  const refreshStyles = useCallback(async () => {
    await loadStyles();
  }, [loadStyles]);

  // Combine all items with type information
  const allItems: WritingStyleItem[] = [
    ...personalStyles.map(s => ({ ...s, profileType: 'personal' as const })),
    ...brandProfiles.map(b => ({ ...b, profileType: 'brand' as const }))
  ];

  // Get filtered items based on tab selection
  const getFilteredItems = useCallback((filter: 'all' | 'personal' | 'brands'): WritingStyleItem[] => {
    const combined: WritingStyleItem[] = [
      ...personalStyles.map(s => ({ ...s, profileType: 'personal' as const })),
      ...brandProfiles.map(b => ({ ...b, profileType: 'brand' as const }))
    ];
    
    if (filter === 'all') return combined;
    if (filter === 'brands') return combined.filter(item => item.profileType === 'brand');
    if (filter === 'personal') return combined.filter(item => item.profileType === 'personal');
    return combined;
  }, [personalStyles, brandProfiles]);

  // Helper: Get style by ID and type
  const getStyleById = useCallback((id: string, type: 'personal' | 'brand'): PersonalWritingStyle | BrandProfile | undefined => {
    if (type === 'personal') {
      return personalStyles.find(s => s.id === id);
    } else {
      return brandProfiles.find(b => b.id === id);
    }
  }, [personalStyles, brandProfiles]);

  // Helper: Get branch by ID
  const getBranchById = useCallback((id: string): BrandProfileBranch | undefined => {
    return brandProfileBranches.find(b => b.id === id);
  }, [brandProfileBranches]);

  // Load styles on mount and when user changes
  useEffect(() => {
    if (!authLoading) {
      loadStyles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]); // Only depend on authLoading and user, not loadStyles

  const value: WritingStylesContextType = {
    personalStyles,
    brandProfiles,
    brandProfileBranches,
    allItems,
    totalCount: allItems.length,
    loading,
    loadStyles,
    deleteStyle,
    refreshStyles,
    getFilteredItems,
    getStyleById,
    getBranchById,
  };

  return (
    <WritingStylesContext.Provider value={value}>
      {children}
    </WritingStylesContext.Provider>
  );
};

// Hook to use WritingStyles Context
export const useWritingStylesContext = () => {
  const context = useContext(WritingStylesContext);
  if (context === undefined) {
    throw new Error('useWritingStylesContext must be used within a WritingStylesProvider');
  }
  return context;
};
